import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { NativeCollabQuery as NativeCollabQueryResponse } from './__types__/NativeCollabQuery.ts';
import type { useNativeCollabFragment_content$key } from './__generated__/useNativeCollabFragment_content.graphql.ts';

// Native-collab fields used by EditorContentUnifiedQuery. Keep labels on the ordered field key used
// by the other unified-query fragments.
const nativeCollabFragmentNode: GraphQLTaggedNode = graphql`
	fragment useNativeCollabFragment_content on Content @catch {
		id
		type
		status
		version {
			number
			ncsStepVersion
			by {
				type
				... on KnownUser {
					accountId
				}
			}
		}
		history {
			createdBy {
				displayName
			}
			createdDate
		}
		space {
			id
			name
			homepageId
		}
		labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
			nodes {
				id
				name
			}
		}
		subType
	}
`;

/**
 * The native collab page metadata from EditorContentUnifiedQuery in the shape of NativeCollabQuery
 * data. When the fragment is provided, native collab skips its initial NativeCollabQuery fetch.
 */
export function useNativeCollabFragment({
	contentId,
}: {
	contentId: string;
}): EditorContentFragmentResult<NativeCollabQueryResponse> {
	return useEditorContentFragment<useNativeCollabFragment_content$key, NativeCollabQueryResponse>(
		'nativeCollabFragment',
		nativeCollabFragmentNode,
		{
			contentId,
			mapData: (value) => ({ content: { nodes: [value] } }),
		},
	);
}
